import React, { Fragment } from "react";
import { DragDropContext, Droppable } from "react-beautiful-dnd";
import { useFormikContext } from "formik";
import { STATE_KEYS } from "utils/constants/state";
import get from "lodash/get";
import compact from "lodash/compact";
import DraggableFieldGroupItem from "./DraggableFieldGroupItem";
import { listDragProps, LIST_DESTINATIONS } from "utils/drag";
import { reorderModelsCollection, addModel } from "utils/drag";
import Analytics, { DRAG_FIELD_GROUP, SHOW_PRODUCT } from "utils/analytics";
import MenuNavHeader from "components/MenuNavHeader";
import {
  BUTTON_CLASS_CONTEXT,
  BUTTON_TAGS,
  SLIM_CTA_PROMPT_CLASSES,
  UI_THEME
} from "utils/constants/ui";
import CTA from "components/CTA";
import { getFieldPath } from "utils/form";
import { getSchemaValuesForEditor } from "utils/schema";
import { usePageBuilderContext } from "utils/context";
import DebounceTextarea from "components/DebounceTextarea";
import { useIsManifestEditMode } from "utils/hooks/manifest";
import SlimCTA from "components/SlimCTA";

/**
 * Ordered and draggable list of field groups
 * - click to add another group
 * - click on group to see group fields list
 *
 * @param {Object} props
 * @param {String} props.name - manifest.data.content.1b46fe52-0377-419c-9d49-791b728c3296.data.fieldGroups
 * @returns
 */
const FieldGroupOrderSettings = (props) => {
  const name = props.name;
  const schema = props.schema;
  const pageContext = usePageBuilderContext();
  const tracking = pageContext ? pageContext.tracking : {};

  const propertyKey = "fieldGroups";
  const { rootPath, template } = getSchemaValuesForEditor({
    name,
    propertyKey,
    schema
  });

  const {
    values,
    setValues,
    setFieldValue,
    setFieldTouched
  } = useFormikContext();

  const titlePath = getFieldPath(rootPath, "title");
  const subtitlePath = getFieldPath(rootPath, "subtitle");
  const fieldGroupsPath = getFieldPath(rootPath, propertyKey);

  const title = get(values, titlePath);
  const subtitle = get(values, subtitlePath);
  const fieldGroups = get(values, fieldGroupsPath, []);
  const hasModels = fieldGroups.length > 0;
  const isEditMode = useIsManifestEditMode();

  /**
   * {
   *  title: 'Checkout',
   *  subtitle: 'Select your plan to get',
   *  fields: []
   * }
   */
  const fieldGroupNodes = compact(fieldGroups).map(
    (fieldGroup, fieldGroupIndex) => {
      const itemProps = {
        name,
        uid: fieldGroup.uuid,
        title: fieldGroup.title,
        subtitle: fieldGroup.subtitle,
        images: fieldGroup.images,
        index: fieldGroupIndex,
        canRemove: true && !isEditMode,
        canAdd: false && !isEditMode,
        dragDisabled: isEditMode,
        onClick: () => {
          Analytics.track(SHOW_PRODUCT, {
            ...tracking,
            fieldGroup: fieldGroup.uuid
          });
          setFieldValue(STATE_KEYS.EDITOR.MENU_ACTIVE_INDEX, fieldGroupIndex);
        }
      };

      return (
        <DraggableFieldGroupItem
          key={`${fieldGroup.uuid}-${fieldGroupIndex}`}
          {...itemProps}
        />
      );
    }
  );

  const addFieldGroup = addModel({
    values,
    setValues,
    setFieldTouched,
    template,
    modelsPath: fieldGroupsPath,
    stateKey: STATE_KEYS.EDITOR.MENU_ACTIVE_INDEX
  });

  return (
    <Fragment>
      <MenuNavHeader label="Form" />

      <div className="w-100 ph3 pt3">
        <label className="f6 mb2 dib fw6">Title</label>
        <DebounceTextarea
          inputValue={title}
          placeholder="Field group title"
          debounce={500}
          minRows={1}
          disabled={isEditMode}
          name={titlePath}
          valueSet={(val) => {
            setFieldValue(titlePath, val);
            setFieldTouched(titlePath, true);
          }}
        />
      </div>
      <div className="w-100 ph3 pt3">
        <label className="f6 mb2 dib fw6">Subtitle</label>
        <DebounceTextarea
          inputValue={subtitle}
          placeholder="Field group subtitle"
          debounce={500}
          disabled={isEditMode}
          minRows={1}
          name={subtitlePath}
          valueSet={(val) => {
            setFieldValue(subtitlePath, val);
            setFieldTouched(subtitlePath, true);
          }}
        />
      </div>
      <div className="flex flex-row justify-between items-center ph3 pt3 pb2">
        <div className="fw6 f6">Field groups</div>
        {hasModels ? (
          <div
            className="link__decoration--hover color-primary pointer fw6 f6"
            onClick={addFieldGroup}
          >
            Add group
          </div>
        ) : null}
      </div>
      {hasModels ? (
        <DragDropContext
          onDragEnd={reorderModelsCollection({
            setFieldValue,
            setFieldTouched,
            models: fieldGroups,
            modelsPath: fieldGroupsPath,
            tracking: {
              event: DRAG_FIELD_GROUP,
              data: {
                ...tracking
              }
            }
          })}
        >
          <Droppable droppableId={LIST_DESTINATIONS.PRODUCT_COLLECTION}>
            {(provided, snapshot) => (
              <ul {...listDragProps({ provided, snapshot })}>
                {fieldGroupNodes}
                {provided.placeholder}
              </ul>
            )}
          </Droppable>
        </DragDropContext>
      ) : (
        <div className="ph3">
          <SlimCTA
            customClasses={{
              container:
                "w-100 flex flex-column flex-row-ns justify-between ba br2 hairline-1-dashed pa3 items-start items-center-ns bg-white",
              header: "f5 fw6",
              subheader: "f6 lh-copy"
            }}
            subheader="Add a field group to this form."
            actions={[
              {
                copy: "Add",
                tag: BUTTON_TAGS.BUTTON,
                theme: UI_THEME.SLIM,
                onClick: addFieldGroup,
                classes: {
                  contextClass: BUTTON_CLASS_CONTEXT.SECONDARY
                }
              }
            ]}
          />
        </div>
      )}
    </Fragment>
  );
};

export default FieldGroupOrderSettings;
